// @flow
import React, {useMemo} from 'react';
import QuantitySelector from '@homeaway/react-quantity';
import {triggerSelectUnselectFilterEdapEvent} from '../../utils/filtersEdapUtils';
import {isEqual} from 'lodash';
import {compose} from 'redux';
import {connect} from 'react-redux';
import searchParamsActions from '../../actions/searchParams';
import './BedsFilter.less';

import type {FilterCheckType} from '../../actions/searchParams';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import type {Refinement} from '../FilterGroup/FilterGroup';
import type {State as ReduxState} from '../../reducers';
import type {SearchParamsType} from '../../reducers/searchParams';

type LabelComponentProps = {
    data: {
        label: string,
        value: string
    }
};

type StateProps = {
    inputParams: SearchParamsType
};

type DispatchProps = {
    updateMultipleFilterCheck: typeof searchParamsActions.updateMultipleFilterCheck
};

type Props = {
    className: string,
    bedsFilterGroup: FilterGroupType
} & StateProps &
    DispatchProps;

export const LabelComponent = ({data: {label, value}}: LabelComponentProps) => {
    const labelComp =
        label !== '' ? (
            <span className="BedsFilter__label">{label}</span>
        ) : null;
    return (
        <div>
            <span className="BedsFilter__value">{value}</span>
            {labelComp}
        </div>
    );
};

export function bedsQuantityFromInputParams(
    inputParams: SearchParamsType,
    sortedBedsFilter: Refinement[]
): number {
    const {filter} = inputParams;

    // if input params have no filters applied, beds filter should default to 1
    if (!filter || filter.length === 0) {
        return 1;
    }

    // Find the lowest-indexed beds filter present in the inputParams filter
    // array. The lowest-numbered index should win, since if both 2+ and 5+ were
    // selected, 2+ encompasses both.
    const lowestBedsIndex = sortedBedsFilter.findIndex((bedFilter) =>
        filter.includes(bedFilter.filter.refineByQueryArgument)
    );

    // If no beds filters are present, default to 1
    if (lowestBedsIndex === -1) {
        return 1;
    }

    // If a beds filter was found, convert from 0-based index to a counting
    // number by adding 1.
    return lowestBedsIndex + 1;
}

function filterChecksFromQuantity(
    n: number,
    bedsFilters: Refinement[]
): FilterCheckType[] {
    const updatedFilters = bedsFilters.map((filter) => {
        let currentSelected = bedsFilters[n - 1];
        let updatedFilter = {
            checked: false,
            name: filter.filter.refineByQueryArgument
        };
        if (isEqual(currentSelected.filter, filter.filter)) {
            updatedFilter.checked = true;
        }

        return updatedFilter;
    });
    return updatedFilters;
}

function BedsFilter(props: Props) {
    const sortedBedsFilters = useMemo(
        () =>
            [...props.bedsFilterGroup.filters].sort((a, b) =>
                a.filter.id > b.filter.id ? 1 : -1
            ),
        [props.bedsFilterGroup]
    );

    const bedsValue = bedsQuantityFromInputParams(
        props.inputParams,
        sortedBedsFilters
    );

    function handleBedsChange(bedsCount: number) {
        triggerSelectUnselectFilterEdapEvent({
            eventaction: 'filter selected',
            eventlabel: `beds_min_${bedsCount}`
        });

        props.updateMultipleFilterCheck(
            filterChecksFromQuantity(bedsCount, sortedBedsFilters)
        );
    }

    const {className, bedsFilterGroup} = props;
    return (
        sortedBedsFilters &&
        bedsValue && (
            <div role="menuitem" className={`BedsFilter ${className}`}>
                <div className="BedsFilter__QuantitySelector BedsFilter--row">
                    <QuantitySelector
                        increaseLabel={'Increase beds'}
                        decreaseLabel={'Decrease beds'}
                        id={`BedsFilter__quantity_${className}`}
                        value={bedsValue}
                        onIncrement={handleBedsChange}
                        onDecrement={handleBedsChange}
                        max={6}
                        min={1}
                        label={LabelComponent}
                        labelData={{
                            value: `${bedsValue}+`,
                            label: bedsFilterGroup.groupInfo.name
                        }}
                    />
                </div>
            </div>
        )
    );
}

export const mapStateToProps = (state: ReduxState): StateProps => {
    return {
        inputParams: state.searchParams.inputParams
    };
};

const mapDispatchToProps = {
    updateMultipleFilterCheck: searchParamsActions.updateMultipleFilterCheck
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
    BedsFilter
);
